import { useState } from 'react';
import FranchiseeNotesDrawer from './FranchiseeNotesDrawer';
import LocationAddress from './LocationAddress';
import { useFranchiseeNotesQuery } from '../api/queries';
import { useAuth } from '../hooks/useAuth';
import { displayUrl, makeHref, splitEmails } from '../lib/format';
import type { Franchisee } from '../types';

type Props = { franchisee: Franchisee };

export default function FranchiseeProfile({ franchisee: f }: Props) {
  const gmbProfileUrl = f.gmb_link ? f.gmb_link.replace(/\/review\/?$/, '') : '';
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [notesOpen, setNotesOpen] = useState(false);

  // Fetched up front (not only while the drawer is open) so the button can
  // carry a count — an admin should see that an account has notes without
  // having to open it. Admin-only, so franchisees never fire this request.
  const notesQuery = useFranchiseeNotesQuery(f.af_number, isAdmin);
  const noteCount = notesQuery.data?.notes.length ?? 0;

  const displayName = f.marketing_name ?? f.territory_name ?? f.af_number;

  return (
    <>
      <div className="profile-nav">
        <img src="/amada-logo.png" alt="Amada Senior Care" className="profile-logo" />
        <div className="profile-nav-right">
          {isAdmin && (
            <button
              className="profile-notes-btn admin-only"
              onClick={() => setNotesOpen(true)}
              title="Internal notes — admins only"
            >
              Notes
              {noteCount > 0 && <span className="profile-notes-count">{noteCount}</span>}
            </button>
          )}
          <div className="profile-nav-text">
            <div className="profile-nav-territory">{f.marketing_name ?? f.territory_name ?? ''}</div>
            <div className="profile-nav-meta">
              <span className="profile-nav-owner">
                {[f.first_name, f.last_name].filter(Boolean).join(' ')}
              </span>
              <span className="profile-nav-af">{f.af_number}</span>
            </div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <FranchiseeNotesDrawer
          af={f.af_number}
          label={displayName}
          open={notesOpen}
          onClose={() => setNotesOpen(false)}
        />
      )}

      <div className="cards-row">
        <div className="card">
          <div className="card-title">Web Presence</div>
          <Field label="Vanity URL">
            <Link href={f.vanity_url} text={displayUrl(f.vanity_url ?? '')} />
          </Field>
          <Field label="Site URL">
            <Link href={f.site_url} text={f.site_url ?? ''} />
          </Field>
          <Field label="Google Business Profile">
            <Link href={gmbProfileUrl} text={gmbProfileUrl ? 'View GMB Profile' : ''} />
          </Field>
        </div>

        <div className="card">
          <div className="card-title">Critical Info</div>
          <Field label="Webform Distribution Emails">
            <Emails value={f.webform_emails} />
          </Field>
          <Field label="Location Address">
            <LocationAddress af={f.af_number} />
          </Field>
        </div>

        <div className="card">
          <div className="card-title">PPC Campaign</div>
          <Field label="Active PPC?">
            <PpcBadge value={f.active_ppc} />
          </Field>
          <Field label="CallRail Number">
            {f.ppc_callrail ? (
              <a href={`tel:${f.ppc_callrail}`}>{f.ppc_callrail}</a>
            ) : (
              <span className="empty">Not assigned</span>
            )}
          </Field>
          <Field label="Landing Page">
            <Link href={f.ppc_lp} text={f.ppc_lp ? 'View LP' : ''} />
          </Field>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div className="field-value">{children}</div>
    </div>
  );
}

function Link({ href, text }: { href: string | null | undefined; text: string }) {
  const finalHref = makeHref(href);
  if (!finalHref || !text) return <span className="empty">Not configured</span>;
  return (
    <a href={finalHref} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );
}

function Emails({ value }: { value: string | null | undefined }) {
  const list = splitEmails(value);
  if (list.length === 0) return <span className="empty">None</span>;
  return (
    <>
      {list.map((email, i) => (
        <span key={email}>
          <a href={`mailto:${email}`}>{email}</a>
          {i < list.length - 1 && <br />}
        </span>
      ))}
    </>
  );
}

function PpcBadge({ value }: { value: string | null | undefined }) {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return <span className="empty">Not configured</span>;
  const isYes = ['yes', 'y', 'true', '1', 'active'].includes(v);
  const isNo = ['no', 'n', 'false', '0', 'inactive'].includes(v);
  if (isYes) {
    return <span className="ppc-badge ppc-yes">Yes — Active</span>;
  }
  if (isNo) {
    return <span className="ppc-badge ppc-no">No</span>;
  }
  return <span>{value}</span>;
}
